/**
 * The farm map, as on AI Bhoomi, with onboarding painted on it.
 *
 * Every khasra in the record is a faint hairline. A farm is drawn, in colour,
 * once anyone has touched its holder (phone, invite, a WhatsApp pin...) or
 * once it is clicked - so the map fills in as the programme does, instead of
 * starting as a quilt of 12,000 polygons nobody has looked at. The colour is
 * the holder's onboarding stage; one toggle paints the AI Bhoomi segments
 * instead. WhatsApp pins are dots: green inside the holder's own khasra,
 * amber in someone else's, red outside every farm in the record.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl, { type ExpressionSpecification, type Map as MapLibreMap } from 'maplibre-gl';
import type { Holder, Segment, Stage } from '@shared/model';
import { SEGMENT_COLOR, SEGMENT_LABEL, STAGES, STAGE_COLOR, STAGE_LABEL, stageIndex } from '@shared/model';
import { buildStyle } from '../map/basemap';
import { FarmerCard } from '../components/FarmerCard';
import { StagePill } from '../components/StagePill';
import { StateBlock } from '../components/StateBlock';
import { loadFarms, useRoster } from '../state/RosterContext';
import type { Loadable } from '../api/client';

const SOURCE = 'farms';
type ColourMode = 'stage' | 'segment';

const stageColour: ExpressionSpecification = [
  'match', ['coalesce', ['feature-state', 'stage'], 'target'],
  ...STAGES.flatMap((s) => [s, STAGE_COLOR[s]]),
  STAGE_COLOR.target,
] as unknown as ExpressionSpecification;
const segmentColour: ExpressionSpecification = ['match', ['get', 'segment'], 'marginal', SEGMENT_COLOR.marginal, 'small', SEGMENT_COLOR.small, 'medium', SEGMENT_COLOR.medium, '#6b7280'];

const empty = (): GeoJSON.FeatureCollection => ({ type: 'FeatureCollection', features: [] });

export function FarmMap({ holderId }: { holderId: string | null }): JSX.Element {
  const { holders, byId, rows, unmatched, villages } = useRoster();
  const holder = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const [data, setData] = useState<Loadable<GeoJSON.FeatureCollection>>({ status: 'loading' });
  const [ready, setReady] = useState(false);
  const [selected, setSelected] = useState<string | null>(holderId);
  const [clicked, setClicked] = useState<Set<string>>(new Set());
  const [village, setVillage] = useState('all');
  const [stageFilter, setStageFilter] = useState<Stage | 'all' | 'touched'>('all');
  const [segment, setSegment] = useState<Segment | 'all'>('all');
  const [mode, setMode] = useState<ColourMode>('stage');
  const [q, setQ] = useState('');
  const [drawAll, setDrawAll] = useState(false);
  const [vra, setVra] = useState<GeoJSON.FeatureCollection | null>(null);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    (map.getSource('vra') as maplibregl.GeoJSONSource | undefined)?.setData(vra ?? empty());
  }, [vra, ready]);

  useEffect(() => {
    let live = true;
    loadFarms().then((fc) => live && setData({ status: 'ready', data: fc })).catch((e: Error) => live && setData({ status: 'error', message: e.message }));
    return () => { live = false; };
  }, []);

  useEffect(() => { if (holderId) setSelected(holderId); }, [holderId]);

  /** The stage painted on each khasra: the furthest of its holders. */
  const pidStage = useMemo(() => {
    const m = new Map<string, Stage>();
    for (const r of rows.values()) {
      const h = byId.get(r.holder_id);
      if (!h) continue;
      for (const f of h.farms) {
        const cur = m.get(f.pid);
        if (!cur || stageIndex(r.stage) > stageIndex(cur)) m.set(f.pid, r.stage);
      }
    }
    return m;
  }, [rows, byId]);

  const sel: Holder | null = selected ? byId.get(selected) ?? null : null;

  // ------------------------------------------------------------ the map
  useEffect(() => {
    if (!holder.current || mapRef.current || data.status !== 'ready') return;
    const map = new maplibregl.Map({ container: holder.current, style: buildStyle(null), center: [74.80, 24.58], zoom: 11.6, attributionControl: { compact: true } });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    map.on('load', () => {
      map.addSource(SOURCE, { type: 'geojson', data: data.data, promoteId: 'pid' });
      map.addLayer({ id: 'farm-hit', type: 'fill', source: SOURCE, paint: { 'fill-color': '#000', 'fill-opacity': 0 } });
      map.addLayer({ id: 'farm-hint', type: 'line', source: SOURCE, paint: { 'line-color': 'rgba(255,255,255,0.30)', 'line-width': 0.6 } });
      map.addLayer({ id: 'farm-fill', type: 'fill', source: SOURCE, filter: ['boolean', false], paint: { 'fill-color': stageColour, 'fill-opacity': ['case', ['boolean', ['feature-state', 'selected'], false], 0.75, 0.42] } });
      map.addLayer({ id: 'farm-line', type: 'line', source: SOURCE, filter: ['boolean', false], layout: { 'line-join': 'round', 'line-cap': 'round' }, paint: { 'line-color': ['case', ['boolean', ['feature-state', 'selected'], false], '#ffffff', stageColour], 'line-width': ['case', ['boolean', ['feature-state', 'selected'], false], 2.6, 1.5] } });
      map.addSource('boundary', { type: 'geojson', data: empty() });
      map.addLayer({ id: 'boundary-line', type: 'line', source: 'boundary', paint: { 'line-color': '#ffffff', 'line-width': 2, 'line-dasharray': [2, 1.5] } });
      map.addSource('farm-pins', { type: 'geojson', data: empty() });
      map.addLayer({ id: 'farm-pins', type: 'circle', source: 'farm-pins', paint: { 'circle-radius': 4, 'circle-color': '#ffffff', 'circle-stroke-color': '#0b1014', 'circle-stroke-width': 1.5 } });
      // The VRA zones for one khasra, drawn over the record when the card makes a map.
      map.addSource('vra', { type: 'geojson', data: empty() });
      map.addLayer({ id: 'vra-fill', type: 'fill', source: 'vra', paint: { 'fill-color': ['coalesce', ['get', 'colour'], '#888'], 'fill-opacity': 0.55 } });
      map.addLayer({ id: 'vra-line', type: 'line', source: 'vra', paint: { 'line-color': '#0b1014', 'line-width': 1 } });
      map.addSource('wa-pins', { type: 'geojson', data: empty() });
      map.addLayer({ id: 'wa-pins', type: 'circle', source: 'wa-pins', paint: { 'circle-radius': ['case', ['boolean', ['get', 'selected'], false], 9, 6.5], 'circle-color': ['match', ['get', 'kind'], 'own', '#4edea3', 'other', '#fab219', '#f87171'], 'circle-stroke-color': '#0b1014', 'circle-stroke-width': 1.5 } });

      map.on('click', 'farm-hit', (e) => {
        const f = e.features?.[0];
        if (!f) return;
        const pid = String((f.properties as { pid: string }).pid);
        let hs: string[] = [];
        try { hs = JSON.parse(String((f.properties as { holders?: string }).holders ?? '[]')) as string[]; } catch { /* no holders on this khasra */ }
        setClicked((prev) => (prev.has(pid) ? prev : new Set(prev).add(pid)));
        // Prefer a holder already linked on WhatsApp; else the first named.
        const linked = hs.find((id) => rows.has(id));
        setSelected(linked ?? hs[0] ?? null);
      });
      map.on('click', 'wa-pins', (e) => {
        const f = e.features?.[0];
        const id = (f?.properties as { holder_id?: string } | undefined)?.holder_id;
        if (id) setSelected(id);
      });
      for (const layer of ['farm-hit', 'wa-pins']) {
        map.on('mouseenter', layer, () => { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', layer, () => { map.getCanvas().style.cursor = ''; });
      }
      setReady(true);
    });
    return () => { map.remove(); mapRef.current = null; setReady(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Stage per khasra as feature state, and the colour mode.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || data.status !== 'ready') return;
    for (const f of data.data.features) {
      const pid = String((f.properties as { pid: string }).pid);
      map.setFeatureState({ source: SOURCE, id: pid }, { stage: pidStage.get(pid) ?? 'target', selected: Boolean(sel?.farms.some((x) => x.pid === pid)) });
    }
    map.setPaintProperty('farm-fill', 'fill-color', mode === 'stage' ? stageColour : segmentColour);
    map.setPaintProperty('farm-line', 'line-color', ['case', ['boolean', ['feature-state', 'selected'], false], '#ffffff', mode === 'stage' ? stageColour : segmentColour]);
  }, [ready, data, pidStage, mode, sel]);

  // Filters on the layers: scope by village/segment/stage; draw the touched + clicked + selected.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const scope: unknown[] = ['all'];
    if (village !== 'all') scope.push(['==', ['get', 'village'], village]);
    if (segment !== 'all') scope.push(['==', ['get', 'segment'], segment]);
    const scopeFilter = (scope.length > 1 ? scope : ['all']) as never;
    map.setFilter('farm-hit', scopeFilter);
    map.setFilter('farm-hint', scopeFilter);
    let shown: string[];
    if (drawAll) shown = [];
    else {
      const set = new Set<string>([...clicked, ...(sel?.farms.map((f) => f.pid) ?? [])]);
      for (const [pid, st] of pidStage) {
        if (stageFilter === 'all' || stageFilter === 'touched' || st === stageFilter) set.add(pid);
      }
      shown = [...set];
    }
    const drawnFilter = drawAll
      ? scopeFilter
      : shown.length
        ? ([...(scope as unknown[]), ['in', ['get', 'pid'], ['literal', shown]]] as never)
        : (['boolean', false] as never);
    map.setFilter('farm-fill', drawnFilter);
    map.setFilter('farm-line', drawnFilter);
  }, [ready, village, segment, stageFilter, clicked, sel, pidStage, drawAll]);

  // WhatsApp pins, the selected holder's farm pins and boundary.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const pins: GeoJSON.Feature[] = [];
    for (const r of rows.values()) {
      if (r.pin_lat == null || r.pin_lon == null) continue;
      const h = byId.get(r.holder_id);
      if (village !== 'all' && h?.village !== village) continue;
      pins.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [r.pin_lon, r.pin_lat] }, properties: { holder_id: r.holder_id, kind: r.pin_pid ? (r.pin_inside_own ? 'own' : 'other') : 'outside', selected: r.holder_id === selected } });
    }
    for (const u of unmatched) {
      pins.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [u.lon, u.lat] }, properties: { kind: 'outside', name: u.name, selected: false } });
    }
    (map.getSource('wa-pins') as maplibregl.GeoJSONSource).setData({ type: 'FeatureCollection', features: pins });
    const farmPins: GeoJSON.Feature[] = (sel?.farms ?? []).map((f) => ({ type: 'Feature', geometry: { type: 'Point', coordinates: [f.pin[1], f.pin[0]] }, properties: { pid: f.pid } }));
    (map.getSource('farm-pins') as maplibregl.GeoJSONSource).setData({ type: 'FeatureCollection', features: farmPins });
    const row = sel ? rows.get(sel.id) : null;
    let boundary: GeoJSON.FeatureCollection = empty();
    if (row?.boundary_json) {
      try {
        const ring = (JSON.parse(row.boundary_json) as [number, number][]).map(([lat, lon]) => [lon, lat]);
        if (ring.length >= 3) boundary = { type: 'FeatureCollection', features: [{ type: 'Feature', geometry: { type: 'LineString', coordinates: [...ring, ring[0]!] }, properties: {} }] };
      } catch { /* unreadable boundary: nothing drawn */ }
    }
    (map.getSource('boundary') as maplibregl.GeoJSONSource).setData(boundary);
  }, [ready, rows, unmatched, sel, selected, byId, village]);

  // Fly to the selected holder when they were chosen from outside the map.
  const flownTo = useRef<string | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready || !sel || flownTo.current === sel.id) return;
    flownTo.current = sel.id;
    if (!clicked.has(sel.primary_pid)) map.flyTo({ center: [sel.pin[1], sel.pin[0]], zoom: Math.max(map.getZoom(), 15.5), speed: 1.4 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, sel]);

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (s.length < 2 || holders.status !== 'ready') return [];
    const digits = s.replace(/\D/g, '');
    const out: Holder[] = [];
    for (const h of holders.data) {
      if (village !== 'all' && h.village !== village) continue;
      const row = rows.get(h.id);
      if (h.name.toLowerCase().includes(s) || h.relation.toLowerCase().includes(s) || h.farms.some((f) => f.khasra === s || f.khasra.toLowerCase() === s) || (digits.length >= 4 && (row?.phone ?? '').includes(digits)) || h.id === s) out.push(h);
      if (out.length >= 40) break;
    }
    return out;
  }, [q, holders, rows, village]);

  if (data.status !== 'ready') return <div className="farms-shell"><div style={{ padding: 20 }}><StateBlock state={data} title="Farm map" /></div></div>;

  const touched = pidStage.size;
  return (
    <div className="farms-shell">
      <aside className="farms-side">
        <header className="farms-side__head">
          <h1>Farms in the programme villages</h1>
          <p>{data.data.features.length.toLocaleString('en-IN')} khasras · {holders.status === 'ready' ? holders.data.length.toLocaleString('en-IN') : '…'} farmers · {touched.toLocaleString('en-IN')} khasras with onboarding progress</p>
        </header>

        <div className="search">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a farmer: name, father's name, khasra, phone" />
          {results.length ? (
            <ul className="results">
              {results.map((h) => (
                <li key={h.id} onClick={() => { setSelected(h.id); setQ(''); }}>
                  <span>{h.name}{h.relation ? <span className="muted"> · {h.relation}</span> : null}</span>
                  <span className="muted">{h.village} · {h.farms.length} kh.</span>
                  <StagePill stage={rows.get(h.id)?.stage ?? 'target'} small />
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <div className="farms-filters">
          <label>
            <span>Village</span>
            <select value={village} onChange={(e) => setVillage(e.target.value)}>
              <option value="all">All {villages.length}</option>
              {villages.map((v) => <option key={v.village} value={v.village}>{v.village}</option>)}
            </select>
          </label>
          <label>
            <span>Draw</span>
            <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value as Stage | 'all' | 'touched')}>
              <option value="all">Every touched farm</option>
              {STAGES.filter((s) => s !== 'target').map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
            </select>
          </label>
          <label>
            <span>Segment</span>
            <select value={segment} onChange={(e) => setSegment(e.target.value as Segment | 'all')}>
              <option value="all">All three</option>
              <option value="marginal">Marginal</option>
              <option value="small">Small</option>
              <option value="medium">Medium</option>
            </select>
          </label>
          <label>
            <span>Colour</span>
            <div className="legend-toggle">
              <button type="button" className={`btn-ghost ${mode === 'stage' ? 'is-active' : ''}`} onClick={() => setMode('stage')}>Onboarding</button>
              <button type="button" className={`btn-ghost ${mode === 'segment' ? 'is-active' : ''}`} onClick={() => setMode('segment')}>Segment</button>
            </div>
          </label>
        </div>

        <ul className="map-legend">
          {mode === 'stage'
            ? STAGES.map((s) => <li key={s}><i style={{ background: STAGE_COLOR[s] }} />{STAGE_LABEL[s]}</li>)
            : (['marginal', 'small', 'medium'] as Segment[]).map((s) => <li key={s}><i style={{ background: SEGMENT_COLOR[s] }} />{SEGMENT_LABEL[s]}</li>)}
          <li><i className="dot" style={{ background: '#4edea3' }} />pin in own khasra</li>
          <li><i className="dot" style={{ background: '#fab219' }} />pin in another's khasra</li>
          <li><i className="dot" style={{ background: '#f87171' }} />pin outside the record</li>
          <li><i className="dot" style={{ background: '#fff' }} />farm pin (inside boundary)</li>
        </ul>

        <div className="farms-visible">
          <span className="muted">Faint outlines are every khasra in the record.</span>
          <button type="button" onClick={() => setDrawAll((v) => !v)}>{drawAll ? 'Draw touched only' : 'Draw all shown'}</button>
        </div>

        {sel ? (
          <FarmerCard
            holder={sel}
            onClose={() => setSelected(null)}
            onShowFarm={(_pid, pin) => mapRef.current?.flyTo({ center: [pin[1], pin[0]], zoom: 17 })}
            onZones={(fc, pid) => {
              setVra(fc);
              const f = pid ? sel.farms.find((x) => x.pid === pid) : null;
              if (fc && f) mapRef.current?.flyTo({ center: [f.pin[1], f.pin[0]], zoom: 17.5 });
            }}
          />
        ) : (
          <p className="farms-hint">Click a farm, a WhatsApp pin, or find a farmer by name. The card shows the record, the onboarding stage, the WhatsApp link and the field history.</p>
        )}

        <footer className="farms-foot">
          <p className="farms-muted">Boundaries, owners and areas come from the state revenue record. The white dot on each farm is the coordinate the portal keeps for it, always inside its own boundary. WhatsApp pins come from the AI Bhoomi bot's records.</p>
        </footer>
      </aside>
      <div ref={holder} className="farms-map" />
    </div>
  );
}
